import { CheckCircle } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Heading } from "@tokenterminal/core/ui/heading/Heading"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { SelectField } from "@tokenterminal/core/ui/input/SelectField"
import { TextareaField } from "@tokenterminal/core/ui/input/TextareaField"
import { TextField } from "@tokenterminal/core/ui/input/TextField"
import { useRouter } from "next/router"
import { type FormEvent, useEffect, useState } from "react"
import { Form } from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { ContactLayout } from "./ContactLayout"
import {
  CONTACT_EMAIL,
  type DemoRequest,
  INTEREST_PARAMS,
  INTERESTS,
  ORGANIZATION_TYPES,
  submitDemoRequest,
} from "./contact"

const TELEGRAM_URL = "https://t.me/TokenTerminalHQ"

const selectButtonProps = {
  className: twMerge(
    "w-full font-normal shadow-none",
    // Match the text inputs' fill and border in contact-form.css
    "border-neutral-800/15 bg-neutral-950/[0.03] data-[hovered]:bg-neutral-950/5 dark:border-neutral-500/35 dark:bg-neutral-200/[0.06] dark:data-[hovered]:bg-neutral-200/[0.09]",
    // Grey placeholder, so it does not look like a selected value
    "[&_[data-placeholder]]:text-fg-secondary",
    // The trigger stays "pressed" while open, and the press scale
    // makes a full-width button visibly shrink
    "active:scale-100 data-[pressed]:scale-100"
  ),
}
const selectPopoverProps = { style: { width: "var(--trigger-width)" } }

// `isRequired` accepts a value of only spaces, which is empty once trimmed
const validateNotBlank = (value: string) =>
  value.trim() ? null : "Please fill out this field."

/** The /contact page. Every "Book a demo" and "Contact sales" button links here. */
const ContactUs = () => {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [company, setCompany] = useState("")
  const [organizationType, setOrganizationType] =
    useState<DemoRequest["organizationType"]>()
  const [interest, setInterest] = useState<DemoRequest["interest"]>()
  const [message, setMessage] = useState("")
  const [isOrganizationTypeMissing, setIsOrganizationTypeMissing] =
    useState(false)
  // Errors HubSpot returns, by field name. React Aria clears one when its field changes.
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<
    "idle" | "submitting" | "submitted" | "failed"
  >("idle")

  // The query is empty until the router is ready, so apply it after the first render
  const { query } = useRouter()
  useEffect(() => {
    if (typeof query.interest === "string")
      setInterest(INTEREST_PARAMS[query.interest])
  }, [query.interest])

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // Enter in a text field submits without pressing the button
    if (!organizationType) {
      setIsOrganizationTypeMissing(true)
      return
    }

    setStatus("submitting")
    try {
      const result = await submitDemoRequest({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        company: company.trim(),
        organizationType,
        interest,
        message: message.trim(),
        pageUri: document.referrer || window.location.href,
      })
      setServerErrors(
        result === "invalid-email"
          ? { email: "Please use your work email." }
          : {}
      )
      setStatus(result === "sent" ? "submitted" : "idle")
    } catch {
      setStatus("failed")
    }
  }

  return (
    <ContactLayout>
      {status === "submitted" ? (
        <div className="flex flex-col items-center text-center gap-3">
          <Icon as={CheckCircle} className="h-10 w-10 text-fg-emerald" />
          <Heading level={3} as="h1">
            Thanks, we got your request
          </Heading>
          <p className="text-sm text-fg-secondary">
            We will reply to {email.trim()} within one business day.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Heading level={1} as="h1">
              Talk to our team
            </Heading>
            <p className="text-sm text-fg-secondary">
              Book a demo, or ask us about our API, data downloads,
              integrations, or anything else. We usually reply within one
              business day.
            </p>
          </div>
          <Form
            onSubmit={onSubmit}
            validationErrors={serverErrors}
            className="contact-form flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField
                label="First name"
                name="firstName"
                autoComplete="given-name"
                value={firstName}
                onChange={setFirstName}
                isRequired
                validate={validateNotBlank}
              />
              <TextField
                label="Last name"
                name="lastName"
                autoComplete="family-name"
                value={lastName}
                onChange={setLastName}
                isRequired
                validate={validateNotBlank}
              />
            </div>
            <TextField
              label="Business email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              isRequired
            />
            <TextField
              label="Company"
              name="company"
              autoComplete="organization"
              value={company}
              onChange={setCompany}
              isRequired
              validate={validateNotBlank}
            />
            <SelectField
              label="Organization type"
              placeholder="Select your organization type"
              buttonProps={{
                className: twMerge(
                  selectButtonProps.className,
                  // Same red border as an invalid text input
                  isOrganizationTypeMissing &&
                    "border-red-300 dark:border-red-700"
                ),
              }}
              popoverProps={selectPopoverProps}
              selectedKey={organizationType}
              onSelectionChange={(value) => {
                setOrganizationType(
                  ORGANIZATION_TYPES.find((item) => item === value)
                )
                setIsOrganizationTypeMissing(false)
              }}
              isInvalid={isOrganizationTypeMissing}
              errorMessage={
                isOrganizationTypeMissing
                  ? "Please select your organization type."
                  : undefined
              }
            >
              {ORGANIZATION_TYPES.map((item) => (
                <SelectField.Option key={item} id={item}>
                  {item}
                </SelectField.Option>
              ))}
            </SelectField>
            <SelectField
              label="What are you interested in?"
              placeholder="Select a topic"
              buttonProps={selectButtonProps}
              popoverProps={selectPopoverProps}
              selectedKey={interest}
              onSelectionChange={(value) =>
                setInterest(
                  INTERESTS.find((item) => item.value === value)?.value
                )
              }
            >
              {INTERESTS.map((item) => (
                <SelectField.Option key={item.value} id={item.value}>
                  {item.label}
                </SelectField.Option>
              ))}
            </SelectField>
            <TextareaField
              label="Anything else we should know?"
              name="message"
              value={message}
              onChange={setMessage}
            />
            {status === "failed" && (
              <p className="text-sm text-fg-red">
                Could not send your request. Please email {CONTACT_EMAIL}.
              </p>
            )}
            <Button
              type="submit"
              variant="solid"
              size="large"
              className="w-full"
              loading={status === "submitting"}
              // The select is not part of native form validation, so check it on press,
              // together with the browser's checks of the other fields
              onPress={() => setIsOrganizationTypeMissing(!organizationType)}
            >
              Get in touch
            </Button>
          </Form>
          <p className="text-sm text-fg-secondary text-center">
            Or reach us on{" "}
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Telegram
            </a>{" "}
            or at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>
      )}
    </ContactLayout>
  )
}

export default ContactUs
